import { Platform, Alert } from 'react-native';

/**
 * Production-ready clipboard service for React Native apps
 * Handles both development (web) and production (native) environments
 */
class ClipboardService {
  private static instance: ClipboardService;

  static getInstance(): ClipboardService {
    if (!ClipboardService.instance) {
      ClipboardService.instance = new ClipboardService();
    }
    return ClipboardService.instance;
  }

  /**
   * Copy text to clipboard with environment-aware implementation
   */
  async copyToClipboard(text: string): Promise<boolean> {
    if (!text) {
      console.warn('ClipboardService: No text provided to copy');
      return false;
    }

    try {
      if (Platform.OS === 'web') {
        // Web environment (development/Replit)
        return await this.copyToWebClipboard(text);
      } else {
        // Native environment (production builds for iOS/Android)
        return await this.copyToNativeClipboard(text);
      }
    } catch (error) {
      console.error('ClipboardService: Copy failed:', error);
      return false;
    }
  }

  /**
   * Web clipboard implementation for development
   */
  private async copyToWebClipboard(text: string): Promise<boolean> {
    try {
      // Modern browsers with Clipboard API
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // Fallback for older browsers
      if (document) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        return successful;
      }

      return false;
    } catch (error) {
      console.warn('ClipboardService: Web clipboard failed:', error);
      return false;
    }
  }

  /**
   * Native clipboard implementation for production
   */
  private async copyToNativeClipboard(text: string): Promise<boolean> {
    try {
      // Dynamic import to avoid bundling issues in development
      const Clipboard = await import('expo-clipboard');
      await Clipboard.setStringAsync(text);
      return true;
    } catch (error) {
      console.error('ClipboardService: Native clipboard failed:', error);
      // If native clipboard fails, we still return false but don't crash
      return false;
    }
  }

  /**
   * Read from clipboard (mainly for native environments)
   */
  async readFromClipboard(): Promise<string | null> {
    try {
      if (Platform.OS === 'web') {
        // Web environment
        if (navigator && navigator.clipboard && navigator.clipboard.readText) {
          return await navigator.clipboard.readText();
        }
        return null;
      } else {
        // Native environment
        const Clipboard = await import('expo-clipboard');
        return await Clipboard.getStringAsync();
      }
    } catch (error) {
      console.error('ClipboardService: Read failed:', error);
      return null;
    }
  }

  /**
   * Check if clipboard functionality is available
   */
  isClipboardAvailable(): boolean {
    if (Platform.OS === 'web') {
      return !!(navigator && navigator.clipboard) || !!document;
    } else {
      return true; // Native environments should always support clipboard
    }
  }

  /**
   * High-level method for copying with user feedback
   */
  async copyWithFeedback(text: string, successMessage?: string, fallbackMessage?: string): Promise<void> {
    const defaultSuccessMessage = 'Copiado para a área de transferência!';
    const defaultFallbackMessage = 'Não foi possível copiar. Texto:';

    const success = await this.copyToClipboard(text);
    
    if (success) {
      Alert.alert('Sucesso!', successMessage || defaultSuccessMessage);
    } else {
      // Show the text directly if copying failed
      Alert.alert(
        fallbackMessage || defaultFallbackMessage,
        text,
        [{ text: 'OK', style: 'default' }]
      );
    }
  }
}

// Export singleton instance
export default ClipboardService.getInstance();